import React, { useMemo, useState } from 'react';
import { Box, Button, Container, Stack, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { ThemeProvider } from '@mui/material/styles';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import DainTheme from '../DainTheme';
import { useGetAllSourcesSummary } from '../api/MonitoringApi';
import RoutePaths, { ALL_SOURCES_MONITORING_ID } from '../routes/RoutePaths';
import {
    MONITORING_CONTENT_CONTAINER_SX,
    MONITORING_HEADER_CONTAINER_SX,
} from './monitoringLayout';
import AllSourcesFilters from './AllSourcesFilters';
import { ENABLED_FILTER_ALL, filterSources } from './allSourcesFilterUtils';
import SourceSummaryTable from './SourceSummaryTable';

export default function AllSourcesDetail() {
    const intl = useIntl();
    const navigate = useNavigate();
    const sourcesQuery = useGetAllSourcesSummary();

    const sources = sourcesQuery?.data?.result?.sources ?? [];
    const [enabledFilter, setEnabledFilter] = useState(ENABLED_FILTER_ALL);
    const [selectedStatuses, setSelectedStatuses] = useState([]);
    const filteredSources = useMemo(
        () => filterSources(sources, enabledFilter, selectedStatuses),
        [sources, enabledFilter, selectedStatuses]
    );

    return (
        <ThemeProvider theme={DainTheme.light}>
            <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
                <Box
                    sx={{
                        flex: '0 0 auto',
                        position: 'sticky',
                        top: 0,
                        zIndex: 10,
                        backgroundColor: 'background.paper',
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                        py: 1,
                    }}
                >
                    <Container maxWidth={false} disableGutters sx={MONITORING_HEADER_CONTAINER_SX}>
                        <Stack alignItems="center" spacing={0.5}>
                            <Typography
                                variant="h5"
                                sx={{ fontWeight: 700, color: 'primary.main', fontFamily: 'monospace' }}
                            >
                                {intl.formatMessage({ id: 'monitoring.allSources.title' })}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {intl.formatMessage({ id: 'monitoring.detail.subtitle' })}
                            </Typography>
                        </Stack>
                    </Container>
                </Box>

                <Box sx={{ flex: '1 1 auto', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                    <Container
                        maxWidth={false}
                        disableGutters
                        sx={MONITORING_CONTENT_CONTAINER_SX}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: 2,
                                mb: 1,
                                flexShrink: 0,
                                width: '100%',
                            }}
                        >
                            <Button
                                size="small"
                                startIcon={<ArrowBackIcon />}
                                onClick={() => navigate(RoutePaths.monitoring.root)}
                            >
                                {intl.formatMessage({ id: 'monitoring.detail.back' })}
                            </Button>
                            {sources.length > 0 && (
                                <AllSourcesFilters
                                    enabledFilter={enabledFilter}
                                    onEnabledFilterChange={setEnabledFilter}
                                    selectedStatuses={selectedStatuses}
                                    onSelectedStatusesChange={setSelectedStatuses}
                                />
                            )}
                        </Box>

                        {sourcesQuery.isLoading && (
                            <Typography variant="body2" color="text.secondary" sx={{ my: 2 }}>
                                {intl.formatMessage({ id: 'monitoring.loading' })}
                            </Typography>
                        )}
                        {sourcesQuery.isError && (
                            <Typography variant="body2" color="error" sx={{ my: 2 }}>
                                {intl.formatMessage({ id: 'monitoring.detail.error' })}
                            </Typography>
                        )}
                        {!sourcesQuery.isLoading &&
                            !sourcesQuery.isError &&
                            sources.length === 0 && (
                                <Typography variant="body2" color="text.secondary" sx={{ my: 2 }}>
                                    {intl.formatMessage({ id: 'monitoring.allSources.empty' })}
                                </Typography>
                            )}
                        {sources.length > 0 &&
                            (filteredSources.length === 0 ? (
                                <Typography variant="body2" color="text.secondary" sx={{ my: 2 }}>
                                    {intl.formatMessage({ id: 'monitoring.allSources.filter.empty' })}
                                </Typography>
                            ) : (
                                <Box sx={{ flex: 1, minHeight: 0, width: '100%' }}>
                                    <SourceSummaryTable
                                        sources={filteredSources}
                                        mediaId={ALL_SOURCES_MONITORING_ID}
                                    />
                                </Box>
                            ))}
                    </Container>
                </Box>
            </Box>
        </ThemeProvider>
    );
}
